import React from "react"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { useOrderActivities } from "../providers/hooks/useOrderActivities"
import { ActivitiesTableAdvanced } from "@hydrologiq/shared/components/activities"
import ActivityCreate from "@hydrologiq/shared/components/activities/ActivityCreate"

interface ExpandedOrderActivitiesContainerProps {
  orderId: string
  order: gqlTypes.Order
  onActivityCreated?: () => void
}

/**
 * Container component that manages data fetching for order activities
 * Follows the same pattern as Orders.tsx - fetch at container level, pass as props to table
 *
 * KEY: This component merges activities into the order object so that attribute
 * default value calculators have access to all activities for context calculations
 * (e.g., batch inheritance from filling to collection)
 */
const ExpandedOrderActivitiesContainer = React.memo(
  ({ orderId, order, onActivityCreated }: ExpandedOrderActivitiesContainerProps) => {
    const {
      activities,
      allActivities, // NEW: All activities for merging into order
      pagination,
      loading,
      refreshing,
      error,
      filterOptions,
      fetchPage,
      fetchPageSize,
      refetch,
    } = useOrderActivities({
      orderId,
      fulfilment: true,
      initialPageSize: 5,
      clientSidePagination: true, // NEW: Enable client-side pagination
      cacheFirst: false, // Don't update cache to prevent order quantity_consumed from changing
    })

    const handleActivityCreated = React.useCallback(() => {
      // Refetch to get the new activity
      refetch()
      onActivityCreated?.()
    }, [refetch, onActivityCreated])

    const handleActivityChanged = React.useCallback(() => {
      // Refetch when an activity is updated
      // This will read from cache first (cache-first policy), then fetch fresh data in background
      refetch()
    }, [refetch])

    return (
      <ActivitiesTableAdvanced
        order={order}
        activities={activities}
        loading={loading}
        refreshing={refreshing}
        error={error}
        onRetry={refetch}
        filterOptions={filterOptions}
        onActivityChanged={handleActivityChanged}
        hideColumns={["kind"]}
        prefix="Fulfilment"
        allowDelete
        serverPagination={
          pagination
            ? {
                pagination,
                onPageChange: fetchPage,
                onPageSizeChange: fetchPageSize,
              }
            : undefined
        }
      />
    )
  },
  (prevProps, nextProps) => {
    // Only re-render if orderId or order.id changes
    return prevProps.orderId === nextProps.orderId && prevProps.order.id === nextProps.order.id
  }
)

ExpandedOrderActivitiesContainer.displayName = "ExpandedOrderActivitiesContainer"

export default ExpandedOrderActivitiesContainer
